import { faGripDotsVertical } from "@fortawesome/pro-regular-svg-icons";
import { styled } from "@mui/material/styles";
import {
  TreeItemIcon,
  TreeItemIconSlotProps,
  TreeItemIconSlots,
} from "@mui/x-tree-view";
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemRoot,
} from "@mui/x-tree-view/TreeItem";
import { TreeItemDragAndDropOverlay } from "@mui/x-tree-view/TreeItemDragAndDropOverlay";
import { TreeItemLabelInput } from "@mui/x-tree-view/TreeItemLabelInput";
import { TreeItemProvider } from "@mui/x-tree-view/TreeItemProvider";
import {
  useTreeItem,
  UseTreeItemParameters,
} from "@mui/x-tree-view/useTreeItem";
import React from "react";

import { Icon } from "../Icon";
import { type UseTreeItemRootSlotProps } from "./types";
const StyledRoot = styled(TreeItemRoot)(({ theme }) => ({
  "--TreeView-itemChildrenIndentation": theme.spacing(7),
}));

const StyledContent = styled(TreeItemContent)(({ theme }) => ({
  borderRadius: 6,
  padding: theme.spacing(1),
  width: "auto",

  // Note: formula from the paddingLeft of MUI
  marginLeft:
    "calc(4px + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))",

  "&:hover": {
    color: theme.palette.secondary[1],
    backgroundColor: theme.palette.secondary[5],
  },

  ["&[data-selected],&[data-selected]:hover"]: {
    backgroundColor: theme.palette.secondary[5],
    boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[4]}`,
    color: theme.palette.secondary[1],

    ["&[data-focused]"]: {
      backgroundColor: theme.palette.secondary[5],
    },
  },
}));

const StyledLabel = styled(TreeItemLabel)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  marginLeft: theme.spacing(1.5),
  flexGrow: 1,
}));

const StyledLabelInput = styled(TreeItemLabelInput)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize, // size should be the same as the label
  marginLeft: theme.spacing(1.5),
}));

const DragHandle = styled(TreeItemIconContainer)(({ theme }) => ({
  cursor: "grab",
  color: theme.palette.secondary[4],
  "&:hover": {
    color: theme.palette.secondary[2],
  },
}));

const StyledGroup = styled(TreeItemGroupTransition)(({ theme }) => ({
  color: theme.palette.secondary[3],

  ['& li[role="treeitem"]']: {
    position: "relative",

    "--tree-indicator-color": theme.palette.secondary[4],
    "--tree-indicator-width": "10px",
    "--tree-indicator-indent": theme.spacing(4.5),
    "--tree-indicator-intersection": "45%",

    // mimic ├──
    "&::before": {
      content: "''",
      zIndex: 1,

      position: "absolute",
      left: "var(--tree-indicator-indent)",
      top: 0,
      bottom: 0,

      color: "var(--tree-indicator-color)",
      borderLeft: "1px solid",
    },

    "&:last-child::before": {
      // mimic └── (last child)
      bottom: "var(--tree-indicator-intersection)",
    },

    "&::after": {
      content: "''",

      position: "absolute",
      top: "calc(100% - var(--tree-indicator-intersection))",
      transform: "translateY(-50%)",
      left: "var(--tree-indicator-indent)",
      width: "var(--tree-indicator-width)",

      color: "var(--tree-indicator-color)",
      borderBottom: "1px solid",
    },
  },
}));

interface DirectoryTreeItemProProps extends UseTreeItemParameters {
  slots?: TreeItemIconSlots;
  slotProps?: TreeItemIconSlotProps;
  /** Content rendered after the label (before drag handle) */
  endAdornment?: React.ReactNode;
  onContextMenu?: React.MouseEventHandler<HTMLLIElement>;
}

export const DirectoryTreeItemPro = React.forwardRef(
  (props: DirectoryTreeItemProProps, ref: React.Ref<HTMLLIElement>) => {
    // Note: From documentation: https://mui.com/x/react-tree-view/rich-tree-view/ordering/#only-trigger-the-reordering-from-a-drag-handle

    const {
      id,
      itemId,
      label,
      disabled,
      children,
      slots,
      slotProps,
      endAdornment,
      onContextMenu,
      ...other
    } = props;

    const {
      getContextProviderProps,
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getLabelInputProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
    } = useTreeItem({
      id,
      itemId,
      children,
      label,
      disabled,
      rootRef: ref,
    });

    const { draggable, onDragStart, ...rootProps } = getRootProps(
      other,
    ) as UseTreeItemRootSlotProps<{
      // Note: MUI badly typed this even if this is what they do in the documentation
      draggable?: boolean;
      onDragStart?: React.DragEventHandler;
    }>;

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
      if (!onDragStart) {
        return;
      }

      onDragStart(event);
      if (event.currentTarget.parentElement) {
        event.dataTransfer.setDragImage(
          event.currentTarget.parentElement,
          0,
          0,
        );
      }
    };

    return (
      <TreeItemProvider {...getContextProviderProps()}>
        <StyledRoot {...rootProps} onContextMenu={onContextMenu}>
          <StyledContent {...getContentProps()}>
            {draggable && (
              <DragHandle draggable={draggable} onDragStart={handleDragStart}>
                <Icon icon={faGripDotsVertical} />
              </DragHandle>
            )}
            {status.expandable && ( // Because MUI keep rendering an empty item that take space for no reason
              <TreeItemIconContainer {...getIconContainerProps()}>
                <TreeItemIcon
                  status={status}
                  slots={slots}
                  slotProps={slotProps}
                />
              </TreeItemIconContainer>
            )}
            <TreeItemCheckbox {...getCheckboxProps()} />
            {status.editing ? (
              <StyledLabelInput {...getLabelInputProps()} />
            ) : (
              <StyledLabel {...getLabelProps()} />
            )}
            {endAdornment}
            <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
          </StyledContent>
          <StyledGroup {...getGroupTransitionProps()}>{children}</StyledGroup>
        </StyledRoot>
      </TreeItemProvider>
    );
  },
);
